import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

export const exportClosedAccountsExcelReport = async ({ data, selectedBranches = [], startDate = null, endDate = null, reportType = 'detailed', meta = {}, filters = {} }) => {
    try {
        if (!data || !Array.isArray(data)) {
            throw new Error("Invalid data format");
        }

        const workbook = new ExcelJS.Workbook();
        const sheetName = reportType === 'summary' ? "Closed Accounts Summary" : "Closed Accounts Detailed";
        const sheet = workbook.addWorksheet(sheetName);

        // ===== HEADER SECTION =====
        sheet.mergeCells('A1:J1');
        sheet.getCell('A1').value = reportType === 'summary'
            ? "CLOSED ACCOUNTS SUMMARY REPORT"
            : "CLOSED ACCOUNTS DETAILED REPORT";
        sheet.getCell('A1').font = { bold: true, size: 18, color: { argb: '2F75B5' } };
        sheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

        if (startDate && endDate) {
            sheet.mergeCells('A2:J2');
            sheet.getCell('A2').value = `Period: ${startDate} to ${endDate}`;
            sheet.getCell('A2').font = { bold: true, size: 12 };
            sheet.getCell('A2').alignment = { horizontal: 'center' };
        }

        sheet.mergeCells('A3:J3');
        sheet.getCell('A3').value = reportType === 'summary'
            ? "NOTE: Summary report showing closed accounts aggregated by product type and branch"
            : "NOTE: Detailed report showing individual closed accounts with complete account information";
        sheet.getCell('A3').font = { italic: true, color: { argb: 'FF0000' } };
        sheet.getCell('A3').alignment = { horizontal: 'center' };

        if (selectedBranches.length > 0) {
            sheet.mergeCells('A4:J4');
            sheet.getCell('A4').value = `SELECTED BRANCHES: ${selectedBranches.join(', ')}`;
            sheet.getCell('A4').font = { bold: true };
            sheet.getCell('A4').alignment = { horizontal: 'center' };
        }

        sheet.mergeCells('A5:J5');
        sheet.getCell('A5').value = `Report Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`;
        sheet.getCell('A5').font = { italic: true, size: 10 };
        sheet.getCell('A5').alignment = { horizontal: 'center' };

        sheet.addRow([]);

        if (reportType === 'summary') {
            generateSummaryFormat(sheet, data, selectedBranches);
        } else {
            generateDetailedFormat(sheet, data, selectedBranches);
        }

        // Apply auto filter and frozen views
        sheet.autoFilter = {
            from: { row: 7, column: 1 },
            to: { row: 7, column: 10 }
        };

        sheet.views = [
            { state: 'frozen', xSplit: 0, ySplit: 7 }
        ];

        // ===== FILE NAME AND SAVE =====
        const branchName = selectedBranches.length > 0 ?
            (selectedBranches.length === 1 ? selectedBranches[0].replace(/\s+/g, '_') : 'Multiple_Branches') :
            'All_Branches';
        const dateStr = startDate ? startDate.replace(/-/g, '') : dayjs().format('YYYYMMDD');
        const typeStr = reportType === 'summary' ? 'Summary' : 'Detailed';
        const fileName = `Closed_Accounts_${typeStr}_${branchName}_${dateStr}.xlsx`;

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, fileName);
        return true;

    } catch (error) {
        console.error("Error generating Excel:", error);
        throw new Error("Failed to generate Excel file: " + error.message);
    }
};

const generateSummaryFormat = (sheet, data, selectedBranches) => {
    const headers = [
        'No.',
        'Branch Name',
        'Branch ID',
        'Product Type',
        'Individual Accounts',
        'Individual Rate %',
        'Joint Accounts',
        'Joint Rate %',
        'Total Balance (LKR)',
        'Period Type'
    ];

    const headerRow = sheet.addRow(headers);
    headerRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 11 };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '2F75B5' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = {
            top: { style: 'medium' }, left: { style: 'medium' },
            bottom: { style: 'medium' }, right: { style: 'medium' }
        };
    });

    let totalIndividual = 0;
    let totalJoint = 0;
    let totalBalance = 0;
    let rowNumber = 0;

    const dataByBranch = {};
    data.forEach(item => {
        const key = `${item.branchName}_${item.productType}`;
        if (!dataByBranch[key]) {
            dataByBranch[key] = item;
        }
    });

    selectedBranches.forEach(branchName => {
        const branchProducts = data.filter(item => item.branchName === branchName);

        if (branchProducts.length > 0) {
            branchProducts.forEach(item => {
                rowNumber++;
                const individualCount = item.individualCount || 0;
                const jointCount = item.jointCount || 0;
                const totalClosedBalance = item.totalClosedBalance || 0;

                totalIndividual += individualCount;
                totalJoint += jointCount;
                totalBalance += totalClosedBalance;

                const totalAccounts = individualCount + jointCount;
                const individualRate = totalAccounts > 0 ? (individualCount / totalAccounts) * 100 : 0;
                const jointRate = totalAccounts > 0 ? (jointCount / totalAccounts) * 100 : 0;

                const rowData = [
                    rowNumber,
                    item.branchName || 'N/A',
                    item.branchId || 'N/A',
                    item.productType || 'N/A',
                    individualCount,
                    `${individualRate.toFixed(2)}%`,
                    jointCount,
                    `${jointRate.toFixed(2)}%`,
                    totalClosedBalance,
                    item.periodType || 'Closed Accounts'
                ];

                const row = sheet.addRow(rowData);
                formatSummaryRow(row);
            });
        } else {
            rowNumber++;
            const rowData = [
                rowNumber,
                branchName,
                'N/A',
                'ALL PRODUCTS',
                0,
                '0.00%',
                0,
                '0.00%',
                0,
                'No closed accounts in period'
            ];

            const row = sheet.addRow(rowData);
            row.eachCell((cell, colNumber) => {
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                };
                cell.font = { italic: true, color: { argb: '999999' } };

                if (colNumber === 5 || colNumber === 7) {
                    cell.numFmt = '#,##0';
                    cell.alignment = { horizontal: 'right' };
                } else if (colNumber === 9) {
                    cell.numFmt = '#,##0.00';
                    cell.alignment = { horizontal: 'right' };
                } else if (colNumber === 6 || colNumber === 8) {
                    cell.alignment = { horizontal: 'center' };
                } else if (colNumber === 1) {
                    cell.alignment = { horizontal: 'center' };
                }
            });
        }
    });

    addSummarySection(sheet, totalIndividual, totalJoint, totalBalance);

    // Set column widths for summary
    sheet.getColumn(1).width = 8;   // No.
    sheet.getColumn(2).width = 25;  // Branch Name
    sheet.getColumn(3).width = 12;  // Branch ID
    sheet.getColumn(4).width = 20;  // Product Type
    sheet.getColumn(5).width = 18;  // Individual Accounts
    sheet.getColumn(6).width = 16;  // Individual Rate
    sheet.getColumn(7).width = 15;  // Joint Accounts
    sheet.getColumn(8).width = 12;  // Joint Rate
    sheet.getColumn(9).width = 20;  // Total Balance
    sheet.getColumn(10).width = 25; // Period Type
};

// ===== DETAILED FORMAT =====
const generateDetailedFormat = (sheet, data, selectedBranches) => {
    const headers = [
        'No.',
        'Branch Name',
        'Account Number',
        'Customer Number',
        'NIC Number',
        'Full Name',
        'Product Type',
        'Balance Amount (LKR)',
        'Closed Date',
        'Account Type'
    ];

    const headerRow = sheet.addRow(headers);
    headerRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 11 };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '2F75B5' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = {
            top: { style: 'medium' }, left: { style: 'medium' },
            bottom: { style: 'medium' }, right: { style: 'medium' }
        };
    });

    let rowNumber = 0;
    let totalBalance = 0;

    selectedBranches.forEach(branchName => {
        const branchAccounts = data.filter(item => item.branchName === branchName);

        if (branchAccounts.length > 0) {
            branchAccounts.forEach((item, index) => {
                rowNumber++;
                const balanceAmount = item.balanceAmount || item.finalBalance || 0;
                totalBalance += balanceAmount;

                const isJoint = item.jointCount > 1 || (item.individualCount === 0 && item.jointCount === 0 && item.customerName && item.customerName.includes('&'));
                const accountType = isJoint ? 'Joint' : 'Individual';

                const rowData = [
                    rowNumber,
                    item.branchName || 'N/A',
                    item.accNumber || item.accountNumber || 'N/A',
                    item.cusNo || item.customerNumber || 'N/A',
                    item.nicNo || item.nicNumber || 'N/A',
                    item.fullName || item.customerName || 'N/A',
                    item.productType || item.productName || 'N/A',
                    balanceAmount,
                    item.closedOn ? dayjs(item.closedOn).format('YYYY-MM-DD') : 'N/A',
                    accountType
                ];

                const row = sheet.addRow(rowData);
                formatDetailedRow(row, index);
            });

            // Add branch subtotal
            const branchTotal = branchAccounts.reduce((sum, acc) => sum + (acc.balanceAmount || acc.finalBalance || 0), 0);
            const subtotalRow = sheet.addRow([
                '', `Subtotal - ${branchName}`, '', '', '', '', '', branchTotal, '', ''
            ]);
            subtotalRow.eachCell(cell => {
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E2EFDA' } };
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                };
            });
            subtotalRow.getCell(8).numFmt = '#,##0.00';
            subtotalRow.getCell(8).alignment = { horizontal: 'right' };

            sheet.addRow([]); // Empty row between branches
        }
    });

    addDetailedSummarySection(sheet, rowNumber, totalBalance);

    // Set column widths for detailed
    sheet.getColumn(1).width = 8;   // No.
    sheet.getColumn(2).width = 20;  // Branch Name
    sheet.getColumn(3).width = 18;  // Account Number
    sheet.getColumn(4).width = 15;  // Customer Number
    sheet.getColumn(5).width = 18;  // NIC Number
    sheet.getColumn(6).width = 25;  // Full Name
    sheet.getColumn(7).width = 20;  // Product Type
    sheet.getColumn(8).width = 18;  // Balance Amount
    sheet.getColumn(9).width = 12;  // Closed Date
    sheet.getColumn(10).width = 12; // Account Type
};

// ===== FORMATTING FUNCTIONS =====
const formatSummaryRow = (row) => {
    row.eachCell((cell, colNumber) => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        };

        if (colNumber === 1) {
            cell.alignment = { horizontal: 'center' };
        } else if (colNumber === 5 || colNumber === 7) {
            cell.numFmt = '#,##0';
            cell.alignment = { horizontal: 'right' };
        } else if (colNumber === 9) {
            cell.numFmt = '#,##0.00';
            cell.alignment = { horizontal: 'right' };
            cell.font = { color: { argb: '2E8B57' } };
        } else if (colNumber === 6 || colNumber === 8) {
            cell.alignment = { horizontal: 'center' };
        }
    });
};

const formatDetailedRow = (row, index) => {
    row.eachCell((cell, colNumber) => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        };

        // Alternate row colors
        if (index % 2 === 0) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8F8F8' } };
        }

        if (colNumber === 1) {
            cell.alignment = { horizontal: 'center' };
        } else if (colNumber === 8) {
            cell.numFmt = '#,##0.00';
            cell.alignment = { horizontal: 'right' };
            cell.font = { color: { argb: '2E8B57' } };
        } else if (colNumber === 9 || colNumber === 10) {
            cell.alignment = { horizontal: 'center' };
        }
    });
};

// ===== SUMMARY SECTION =====
const addSummarySection = (sheet, totalIndividual, totalJoint, totalBalance) => {
    sheet.addRow([]);
    sheet.addRow([]);

    const totalAccounts = totalIndividual + totalJoint;
    const individualPercentage = totalAccounts > 0 ? ((totalIndividual / totalAccounts) * 100).toFixed(2) : '0.00';
    const jointPercentage = totalAccounts > 0 ? ((totalJoint / totalAccounts) * 100).toFixed(2) : '0.00';

    const summaryHeader = sheet.addRow(['SUMMARY STATISTICS - CLOSED ACCOUNTS']);
    summaryHeader.eachCell(cell => {
        cell.font = { bold: true, size: 14, color: { argb: '2F75B5' } };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E6F3FF' }
        };
        cell.alignment = { horizontal: 'center' };
        cell.border = {
            top: { style: 'medium' }, left: { style: 'medium' },
            bottom: { style: 'medium' }, right: { style: 'medium' }
        };
    });
    sheet.mergeCells(`A${summaryHeader.number}:J${summaryHeader.number}`);

    const summaryRow1 = sheet.addRow([
        'Total Closed Accounts:', totalAccounts.toLocaleString(),
        '', '',
        'Individual Accounts:', `${totalIndividual.toLocaleString()} (${individualPercentage}%)`,
        '', '',
        'Joint Accounts:', `${totalJoint.toLocaleString()} (${jointPercentage}%)`
    ]);

    const summaryRow2 = sheet.addRow([
        'Total Balance Amount:', `LKR ${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        '', '', '', '', '', '', '', ''
    ]);

    [summaryRow1, summaryRow2].forEach(row => {
        row.eachCell((cell, colNumber) => {
            cell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };
            if (colNumber === 1 || colNumber === 5 || colNumber === 9) {
                cell.font = { bold: true };
            }
            if (colNumber === 2 || colNumber === 6 || colNumber === 10) {
                cell.font = { bold: true, color: { argb: '2E8B57' } };
            }
        });
    });
};

const addDetailedSummarySection = (sheet, totalAccounts, totalBalance) => {
    sheet.addRow([]);

    const summaryHeader = sheet.addRow(['DETAILED REPORT SUMMARY']);
    summaryHeader.eachCell(cell => {
        cell.font = { bold: true, size: 14, color: { argb: '2F75B5' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E6F3FF' } };
        cell.alignment = { horizontal: 'center' };
        cell.border = {
            top: { style: 'medium' }, left: { style: 'medium' },
            bottom: { style: 'medium' }, right: { style: 'medium' }
        };
    });
    sheet.mergeCells(`A${summaryHeader.number}:J${summaryHeader.number}`);

    const summaryRow = sheet.addRow([
        'Total Accounts Closed:', totalAccounts.toLocaleString(),
        '', '', '', '', '',
        'Total Balance:', `LKR ${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    ]);

    summaryRow.eachCell((cell, colNumber) => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        };
        if (colNumber === 1 || colNumber === 9) {
            cell.font = { bold: true };
        }
        if (colNumber === 2 || colNumber === 10) {
            cell.font = { bold: true, color: { argb: '2E8B57' } };
        }
    });
};

// Export as named export
export default exportClosedAccountsExcelReport;